import { create } from 'zustand'
import { User } from 'firebase/auth'
import { FleetSettings, TipsHandlingMode, RevenueShareMode } from '@/types'
import { userProfileService } from '@/services/userProfileService'
import { TaxTableService } from '@/services/taxTableService'

interface ConfigStore {
  // State
  settings: FleetSettings
  loading: boolean
  error: string | null

  // Tax table state
  taxTableLoading: boolean
  taxTableLoaded: boolean

  // Actions
  loadSettings: (user: User) => Promise<void>
  updateSettings: (user: User, settings: Partial<FleetSettings>) => Promise<void>
  resetToDefaults: (user: User) => Promise<void>
  loadTaxTableIfNeeded: (taxCalculationMethod?: 'fixed' | 'table', taxTableName?: string) => Promise<void>
  isTableLoaded: (tableNameOrNumber?: string | number) => boolean
  getAvailableTables: () => Promise<number[]>
}

const DEFAULT_SETTINGS: FleetSettings = {
  driverRevenueShare: 40,
  vacationPayRate: 12,
  taxRate: 25,
  vatRate: 6,
  feeOnTips: 'fleet_commission' as TipsHandlingMode, // Default to fleet commission behavior
  taxCalculationMethod: 'fixed', // Default to fixed percentage method
  taxTableName: 'skattetabell31', // Default tax table
  taxColumn: 1, // Default to column 1
  employerTaxRate: 31.42, // Default arbetsgivaravgift
  includeSalaryCalculationPage: true, // Include detailed calculation page by default
  revenueShareMode: 'single' as RevenueShareMode, // Default to single rate mode
  revenueShareTiers: undefined, // No tiers by default
  salaryEmailTemplate: undefined // Uses default template when undefined
}


export const useConfigStore = create<ConfigStore>((set, get) => ({
  // Initial state
  settings: DEFAULT_SETTINGS,
  loading: false,
  error: null,

  // Tax table initial state
  taxTableLoading: false,
  taxTableLoaded: false,
  
  // Load settings from user profile
  loadSettings: async (user: User) => {
    set({ loading: true, error: null })
    try {
      const userProfile = await userProfileService.getUserProfile(user.uid)
      
      if (userProfile) {
        // Extract fleet settings from user profile with backward compatibility
        const normalizeFeeOnTips = (value?: TipsHandlingMode | boolean): TipsHandlingMode => {
          if (typeof value === 'boolean') {
            return value ? 'fleet_commission' : 'driver_share'
          }
          return value || 'fleet_commission'
        }

        const settings: FleetSettings = {
          driverRevenueShare: userProfile.driverRevenueShare,
          vacationPayRate: userProfile.vacationPayRate,
          taxRate: userProfile.taxRate,
          vatRate: userProfile.vatRate,
          feeOnTips: normalizeFeeOnTips(userProfile.feeOnTips), // Handle backward compatibility
          taxCalculationMethod: userProfile.taxCalculationMethod ?? 'fixed', // Default to fixed if not set
          taxTableName: userProfile.taxTableName ?? 'skattetabell31', // Default tax table
          taxColumn: userProfile.taxColumn ?? 1, // Default to column 1
          employerTaxRate: userProfile.employerTaxRate ?? 31.42, // Default arbetsgivaravgift
          includeSalaryCalculationPage: userProfile.includeSalaryCalculationPage ?? true, // Default to true
          revenueShareMode: userProfile.revenueShareMode ?? 'single', // Default to single rate mode
          revenueShareTiers: userProfile.revenueShareTiers, // Pass tiers as-is
          salaryEmailTemplate: userProfile.salaryEmailTemplate // Custom email template
        }
        set({ settings, loading: false })
      } else {
        // User profile not found, use defaults
        set({ settings: DEFAULT_SETTINGS, loading: false })
      }
    } catch (error) {
      console.error('Failed to load settings:', error)
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load settings',
        loading: false,
        settings: DEFAULT_SETTINGS // Fallback to defaults on error
      })
    }
  },
  
  // Update settings
  updateSettings: async (user: User, newSettings: Partial<FleetSettings>) => {
    set({ loading: true, error: null })
    try {
      const updatedSettings = { ...get().settings, ...newSettings }
      await userProfileService.updateFleetSettings(user.uid, updatedSettings)
      set({ settings: updatedSettings, loading: false })
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update settings',
        loading: false 
      })
    }
  },
  
  // Reset to default settings
  resetToDefaults: async (user: User) => {
    set({ loading: true, error: null })
    try {
      await userProfileService.updateFleetSettings(user.uid, DEFAULT_SETTINGS)
      set({ settings: DEFAULT_SETTINGS, loading: false })
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to reset settings',
        loading: false 
      })
    }
  },

  // Load tax table if needed - part of automatic loading strategy
  // This function is called from App.tsx when settings change to ensure tax tables
  // are loaded before components try to use them, preventing console warnings
  loadTaxTableIfNeeded: async (taxCalculationMethod = 'fixed', taxTableName = 'skattetabell31') => {
    if (taxCalculationMethod !== 'table') {
      return // No need to load table for fixed method
    }

    // Extract table number from tableName (e.g., "skattetabell31" -> 31)
    const tableNumber = parseInt(taxTableName.replace(/\D/g, '')) || 31

    const currentYear = new Date().getFullYear()

    // Check if already loaded
    if (TaxTableService.isTableLoaded(tableNumber, currentYear)) {
      set({ taxTableLoaded: true, taxTableLoading: false })
      return
    }

    set({ taxTableLoading: true })
    try {
      // Load from year-specific JSON file
      await TaxTableService.loadTaxTable(tableNumber, currentYear)
      set({ taxTableLoaded: true, taxTableLoading: false })
    } catch (error) {
      console.error(`Failed to load tax table ${tableNumber} from JSON file:`, error)
      set({ taxTableLoaded: false, taxTableLoading: false })
      throw error
    }
  },

  // Check if tax table is loaded (uses current year)
  isTableLoaded: (tableNameOrNumber = 'skattetabell31') => {
    const currentYear = new Date().getFullYear()
    if (typeof tableNameOrNumber === 'string') {
      const tableNumber = parseInt(tableNameOrNumber.replace(/\D/g, '')) || 31
      return TaxTableService.isTableLoaded(tableNumber, currentYear)
    } else {
      return TaxTableService.isTableLoaded(tableNameOrNumber, currentYear)
    }
  },

  // Get available tax tables for the current year
  getAvailableTables: async () => {
    try {
      return await TaxTableService.getAvailableTables(new Date().getFullYear())
    } catch (error) {
      console.error('Failed to get available tables:', error)
      return [31] // Fallback to table 31
    }
  }
}))